import type { FC, ReactNode } from 'react';

import Avatar from '@/common-components/Avatar';
import { cn } from '@/utils/cn.util';
import type { StudentDetail, StudentListItem } from '@/types/student.types';

import { AccountStatusPill, KycStatusPill } from './StatusPills';
import { TRUST_PILLAR_LABELS } from '@/constants/trustScore.constants';
import TrustScoreBar from './TrustScoreBar';

interface UserDetailDrawerProps {
  selectedUser: StudentListItem | null;
  detail: StudentDetail | undefined;
  isLoadingDetail: boolean;
  isErrorDetail: boolean;
  campusNameById: Record<string, string>;
  onClose: () => void;
  onSuspend: (user: StudentListItem) => void;
  onActivate: (user: StudentListItem) => void;
  suspendingId: string | null;
}

const paise2rupee = (paise: number): string => `₹${(paise / 100).toFixed(2)}`;

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

const InfoRow: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-[var(--color-divider)] py-2.5 last:border-b-0">
    <span className="shrink-0 text-[12.5px] font-medium text-[var(--color-ink-muted)]">
      {label}
    </span>
    <span className="text-right text-[13px] text-[var(--color-ink)]">{value}</span>
  </div>
);

const SectionHeading: FC<{ children: ReactNode }> = ({ children }) => (
  <p className="mb-1 text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-ink-soft)]">
    {children}
  </p>
);

const UserDetailDrawer: FC<UserDetailDrawerProps> = ({
  selectedUser,
  detail,
  isLoadingDetail,
  isErrorDetail,
  campusNameById,
  onClose,
  onSuspend,
  onActivate,
  suspendingId,
}) => {
  const isOpen = selectedUser !== null;
  const isSuspending = !!(selectedUser && suspendingId === selectedUser.id);

  // Use detail when loaded, fall back to list item fields for instant display before fetch resolves.
  const name = selectedUser?.name ?? '';
  const email = selectedUser?.collegeEmail ?? '';
  const kycStatus = detail?.kycStatus ?? selectedUser?.kycStatus;
  const accountStatus = detail?.accountStatus ?? selectedUser?.accountStatus;
  const campusName = selectedUser?.campusId
    ? (campusNameById[selectedUser.campusId] ?? '—')
    : '—';

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/40 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={cn(
          'fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-[480px] flex-col bg-white shadow-2xl',
          'overflow-hidden transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header bar */}
        <div className="flex items-center justify-between border-b border-[var(--color-divider)] px-5 py-4">
          <h2 className="text-[15px] font-bold text-[var(--color-ink)]">User Details</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-[var(--color-ink-muted)] transition-colors hover:bg-[var(--color-surface-muted-2)]"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
              <path d="M3.22 3.22a.75.75 0 0 1 1.06 0L8 6.94l3.72-3.72a.75.75 0 1 1 1.06 1.06L9.06 8l3.72 3.72a.75.75 0 1 1-1.06 1.06L8 9.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06L6.94 8 3.22 4.28a.75.75 0 0 1 0-1.06z" />
            </svg>
          </button>
        </div>

        {/* Profile hero */}
        <div className="flex items-center gap-4 border-b border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-5 py-5">
          <Avatar name={name || '?'} src={detail?.avatarUrl} size="xl" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[17px] font-bold text-[var(--color-ink)]">{name}</div>
            <div className="mt-0.5 truncate text-[12px] text-[var(--color-ink-soft)]">{email}</div>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {accountStatus ? <AccountStatusPill status={accountStatus} /> : null}
              {kycStatus ? <KycStatusPill status={kycStatus} /> : null}
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {isLoadingDetail ? (
            <div className="flex items-center justify-center py-16 text-[13px] text-[var(--color-ink-muted)]">
              Loading details…
            </div>
          ) : isErrorDetail ? (
            <div className="flex items-center justify-center py-16 text-[13px] text-[var(--color-danger-ink)]">
              Failed to load details. Try closing and reopening.
            </div>
          ) : detail ? (
            <>
              {/* Academic Profile */}
              <div className="px-5 py-4">
                <SectionHeading>Academic Profile</SectionHeading>
                <InfoRow label="Campus" value={campusName} />
                <InfoRow label="Year" value={detail.year ?? '—'} />
                <InfoRow label="Stream" value={detail.stream ?? '—'} />
                <InfoRow label="Stay Location" value={detail.stayLocation ?? '—'} />
                <InfoRow label="Mobile" value={detail.mobile ?? '—'} />
              </div>

              <div className="mx-5 border-t border-[var(--color-divider)]" />

              {/* Performance */}
              <div className="px-5 py-4">
                <SectionHeading>Performance</SectionHeading>
                <div className="border-b border-[var(--color-divider)] py-2.5 last:border-b-0">
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-[12.5px] font-medium text-[var(--color-ink-muted)]">
                      Trust Score
                    </span>
                    <span className="text-[13px] font-bold text-[var(--color-ink)]">
                      {detail.trustScore}
                    </span>
                  </div>
                  <TrustScoreBar value={detail.trustScore} showTier />
                  {detail.trust ? (
                    <div className="mt-2.5 flex flex-col gap-1 border-t border-[var(--color-divider)] pt-2">
                      {TRUST_PILLAR_LABELS.map((pillar) => (
                        <div key={pillar.key} className="flex items-center justify-between">
                          <span className="text-[11.5px] text-[var(--color-ink-muted)]">
                            {pillar.label}
                          </span>
                          <span className="text-[11.5px] font-semibold text-[var(--color-ink)]">
                            {detail.trust.pillars[pillar.key]} / 200
                          </span>
                        </div>
                      ))}
                      {detail.trust.milestoneBonus > 0 ? (
                        <div className="flex items-center justify-between">
                          <span className="text-[11.5px] text-[var(--color-ink-muted)]">
                            Milestone bonuses
                          </span>
                          <span className="text-[11.5px] font-semibold text-[var(--color-ink)]">
                            +{detail.trust.milestoneBonus}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <InfoRow label="Rentals Completed" value={detail.rentalsCompleted} />
                <InfoRow
                  label="Rating"
                  value={
                    detail.ratingCount > 0
                      ? `★ ${detail.ratingAvg.toFixed(1)} (${detail.ratingCount} review${detail.ratingCount === 1 ? '' : 's'})`
                      : 'No ratings yet'
                  }
                />
                <InfoRow label="Coin Balance" value={`🪙 ${detail.coinBalance}`} />
              </div>

              <div className="mx-5 border-t border-[var(--color-divider)]" />

              {/* Wallet */}
              <div className="px-5 py-4">
                <SectionHeading>Wallet</SectionHeading>
                <InfoRow label="Available Balance" value={paise2rupee(detail.wallet.availablePaise)} />
                <InfoRow label="In Escrow" value={paise2rupee(detail.wallet.escrowPaise)} />
              </div>

              <div className="mx-5 border-t border-[var(--color-divider)]" />

              {/* Account meta */}
              <div className="px-5 py-4">
                <SectionHeading>Account</SectionHeading>
                <InfoRow label="Joined" value={formatDate(detail.createdAt)} />
                <InfoRow label="Email Verified" value={detail.emailVerified ? '✓ Yes' : '✗ No'} />
                <InfoRow label="Last Updated" value={formatDate(detail.updatedAt)} />
              </div>
            </>
          ) : null}
        </div>

        {/* Footer action */}
        {selectedUser && accountStatus ? (
          <div className="border-t border-[var(--color-divider)] bg-white px-5 py-4">
            {accountStatus === 'active' ? (
              <button
                type="button"
                disabled={isSuspending}
                onClick={() => onSuspend(selectedUser)}
                className="w-full rounded-lg border border-[var(--color-danger)] bg-[var(--color-danger-bg)] py-2.5 text-[13px] font-semibold text-[var(--color-danger-ink)] transition-colors hover:bg-[var(--color-danger)] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSuspending ? 'Suspending…' : 'Suspend Account'}
              </button>
            ) : (
              <button
                type="button"
                disabled={isSuspending}
                onClick={() => onActivate(selectedUser)}
                className="w-full rounded-lg border border-[var(--color-success)] bg-[var(--color-success-bg)] py-2.5 text-[13px] font-semibold text-[var(--color-success-ink)] transition-colors hover:bg-[var(--color-success)] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSuspending ? 'Activating…' : 'Activate Account'}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
};

export default UserDetailDrawer;
